import { useQueryClient } from '@tanstack/react-query'
import { Button, Card, Col, Flex, Form, Input, Row } from 'antd'
import { useEffect, useMemo, useState } from 'react'

import {
  useAccountServiceChangePasswordEndpoint,
  useUserServiceGetUserProfileEndpoint,
  useUserServiceGetUserProfileEndpointKey,
  useUserServiceUpdateUserProfileEndpoint
} from 'src/api/queries'
import { ApiError, UpdateUserProfileRequest } from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import { FormFooter, message, PageBreadcrumbs } from 'src/components'
import { PasswordStrength } from 'src/features/auth/components'
import { useLocales } from 'src/locales'
import { formErrorHandler, toDayjs } from 'src/utils'

import { ProfileSettingsFormFields } from '../components'

export const SettingsPage = () => {
  const { t, onChangeLang } = useLocales()
  const [form] = Form.useForm()
  const [passwordForm] = Form.useForm()

  const queryClient = useQueryClient()

  const [isFormDisabled, setIsFormDisabled] = useState(true)

  const { data, isLoading } = useUserServiceGetUserProfileEndpoint()

  const { mutate, isPending } = useUserServiceUpdateUserProfileEndpoint({
    onSuccess: (_, data) => onMutationSuccess(data.requestBody),
    onError: (e: ApiError) => form?.setFields(formErrorHandler(e.body.errors))
  })

  const { mutate: changePassword, isPending: isChangePasswordPending } =
    useAccountServiceChangePasswordEndpoint({
      onSuccess: () => {
        message.success(t('common.save_success'))
        passwordForm.resetFields()
      },
      onError: (e: ApiError) => form?.setFields(formErrorHandler(e.body.errors))
    })

  const initialValues = useMemo(() => {
    return {
      ...data,
      dateOfBirth: toDayjs(data?.dateOfBirth)
    }
  }, [data])

  const onToggle = () => setIsFormDisabled((prev) => !prev)

  const onCancel = () => {
    onToggle()
    form.setFieldsValue(initialValues)
  }

  const onSubmit = async () => {
    const values = await form.validateFields()
    const requestBody = {
      ...values,
      firstName: undefined,
      lastName: undefined,
      dateOfBirth: undefined,
      gender: undefined,
      idnp: undefined
    }

    mutate({ requestBody })
  }

  const onChangePassword = async () => {
    const values = await passwordForm.validateFields()

    changePassword({
      requestBody: {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
        confirmNewPassword: values.confirmNewPassword
      }
    })
  }

  const onMutationSuccess = (data: UpdateUserProfileRequest) => {
    queryClient.invalidateQueries({
      queryKey: [useUserServiceGetUserProfileEndpointKey]
    })
    message.success(t('common.save_success'))
    onToggle()

    if (data.systemLanguage) {
      onChangeLang(data?.systemLanguage)
    }
  }

  useEffect(() => {
    form.setFieldsValue(initialValues)
  }, [data])

  const extra = isFormDisabled ? (
    <Button onClick={onToggle} type="primary">
      {t('common.edit')}
    </Button>
  ) : undefined

  const passwordWatch = Form.useWatch('newPassword', passwordForm)

  return (
    <PageBreadcrumbs
      title={t('menu.settings')}
      items={[
        {
          title: t('menu.settings')
        }
      ]}
      extra={extra}
    >
      <Flex vertical gap={16}>
        <Card bordered={false} loading={isLoading}>
          <Form
            form={form}
            disabled={isFormDisabled}
            layout="vertical"
            requiredMark={false}
          >
            <ProfileSettingsFormFields />

            <FormFooter
              hidden={isFormDisabled}
              onCancel={onCancel}
              onSave={onSubmit}
              saveLabel={t('common.save')}
              saveConfirmTitle={`${t('common.save')}?`}
              loading={isPending}
            />
          </Form>
        </Card>

        <Card bordered={false} loading={isChangePasswordPending}>
          <Form form={passwordForm} layout="vertical" requiredMark={false}>
            <Flex vertical gap={20}>
              <Row gutter={[24, 24]}>
                <Col xs={24} md={12} xl={8}>
                  <Form.Item
                    name="currentPassword"
                    label={t('form.current_password')}
                    rules={requiredRules}
                  >
                    <Input.Password placeholder={t('form.current_password')} />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12} xl={8}>
                  <Form.Item
                    name="newPassword"
                    label={t('form.new_password')}
                    rules={requiredRules}
                  >
                    <Input.Password placeholder={t('form.new_password')} />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12} xl={8}>
                  <Form.Item
                    name="confirmNewPassword"
                    label={t('form.confirm_password')}
                    rules={[
                      {
                        required: true,
                        message: t('form.confirm_password_error')
                      },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (
                            !value ||
                            getFieldValue('newPassword') === value
                          ) {
                            return Promise.resolve()
                          }
                          return Promise.reject(
                            new Error(t('form.confirm_password_error'))
                          )
                        }
                      })
                    ]}
                  >
                    <Input.Password placeholder={t('form.confirm_password')} />
                  </Form.Item>
                </Col>

                {passwordWatch && (
                  <>
                    <Col span={8} />
                    <Col span={8}>
                      <PasswordStrength value={passwordWatch ?? ''} />
                    </Col>
                    <Col span={8} />
                  </>
                )}
              </Row>

              <Flex justify="end">
                <Button
                  loading={isPending}
                  onClick={onChangePassword}
                  type="primary"
                >
                  {t('form.change_password')}
                </Button>
              </Flex>
            </Flex>
          </Form>
        </Card>
      </Flex>
    </PageBreadcrumbs>
  )
}
